(() => {
  'use strict';

  let refreshTimer;
  let waFallbackTimer;

  // Keep Sleek's native bottom navigation. An older KS helper created a second
  // five-item bar with WhatsApp in the centre; remove that bar whenever found.
  function removeLegacyMobileBottomNav() {
    document.getElementById('KSMobileBottomNav')?.remove();
  }

  // Reuse a WhatsApp element only when it is already a true floating/fixed
  // widget. Normal WhatsApp links elsewhere on the page must never be moved.
  function fixedWhatsappCandidate() {
    const selectors = [
      'a[href*="wa.me"]',
      'a[href*="api.whatsapp.com"]',
      'a[href*="whatsapp.com/send"]',
      'iframe[src*="whatsapp"]',
      '[class*="whatsapp" i]',
      '[id*="whatsapp" i]'
    ];

    for (const selector of selectors) {
      for (const node of document.querySelectorAll(selector)) {
        if (node.closest?.('#KSMobileBottomNav')) continue;
        if (node.classList?.contains('ks-whatsapp-float')) continue;

        let el = node;
        for (let i = 0; el && i < 10; i += 1, el = el.parentElement) {
          if (el.id === 'KSMobileBottomNav') break;
          try {
            if (getComputedStyle(el).position === 'fixed') return el;
          } catch (_) {}
        }
      }
    }
    return null;
  }

  function makeFallback() {
    if (document.querySelector('.ks-whatsapp-float')) return;
    const a = document.createElement('a');
    a.className = 'ks-whatsapp-float';
    a.href = 'https://wa.me/918344333729?text=Hello%20KS%20Textiles%2C%20I%20need%20help%20with%20a%20saree.';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.setAttribute('aria-label', 'Chat with KS Textiles on WhatsApp');
    a.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11.8a8 8 0 0 1-11.8 7L4 20l1.2-4.1A8 8 0 1 1 20 11.8Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M8.2 7.8c.4-.4.8-.2 1 .2l.8 1.7c.2.4.1.7-.2 1l-.5.5c.8 1.6 2 2.8 3.6 3.6l.6-.6c.3-.3.6-.3.9-.1l1.7.8c.4.2.6.6.3 1-.6.9-1.5 1.3-2.6 1.1-3.4-.7-6.1-3.4-6.8-6.8-.2-1 .2-1.9 1.2-2.4Z" fill="currentColor"/></svg>';
    document.body.appendChild(a);
  }

  function manageWhatsApp() {
    removeLegacyMobileBottomNav();

    const existing = fixedWhatsappCandidate();
    const fallback = document.querySelector('.ks-whatsapp-float');

    document.querySelectorAll('.ks-whatsapp-widget').forEach(el => {
      if (el !== existing) el.classList.remove('ks-whatsapp-widget');
    });

    if (existing) {
      existing.classList.add('ks-whatsapp-widget');
      fallback?.remove();
      clearTimeout(waFallbackTimer);
      return;
    }

    clearTimeout(waFallbackTimer);
    if (!fallback) {
      waFallbackTimer = setTimeout(() => {
        removeLegacyMobileBottomNav();
        if (!fixedWhatsappCandidate()) makeFallback();
      }, 500);
    }
  }

  // The cart drawer and sticky ATC are siblings on product pages. Sleek's drawer
  // opens by adding active/open attributes, so mirror that state onto <body>.
  // CSS can then remove the product purchase bar completely while the cart UI is active.
  function syncCartDrawerState() {
    const drawer = document.getElementById('CartDrawer');
    const isOpen = !!drawer && !drawer.hidden && (drawer.hasAttribute('active') || drawer.hasAttribute('open'));
    document.body.classList.toggle('ks-cart-drawer-open', isOpen);
  }

  function installCartDrawerGuard() {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer) {
      document.body.classList.remove('ks-cart-drawer-open');
      return;
    }

    if (drawer.dataset.ksStickyAtcGuard !== '1') {
      drawer.dataset.ksStickyAtcGuard = '1';
      const observer = new MutationObserver(syncCartDrawerState);
      observer.observe(drawer, {
        attributes: true,
        attributeFilter: ['active', 'open', 'hidden']
      });
    }

    syncCartDrawerState();
  }

  // Android browsers can occasionally fail to surface the native picker for a
  // styled <select> inside a transformed fixed bar. Use showPicker() during the
  // user's pointer gesture when supported, while retaining the normal fallback.
  function enhanceStickyVariantPicker() {
    document.querySelectorAll('.sticky-atc-bar__variant-select').forEach((select) => {
      if (select.dataset.ksMobilePickerReady === '1') return;
      select.dataset.ksMobilePickerReady = '1';

      select.addEventListener('pointerdown', (event) => {
        if (!window.matchMedia('(max-width: 767.98px)').matches) return;
        if (select.disabled || typeof select.showPicker !== 'function') return;

        try {
          select.showPicker();
          event.preventDefault();
        } catch (_) {
          // If showPicker is unavailable/blocked, allow the browser's native select behavior.
        }
      }, { passive: false });
    });
  }

  // Shirt-size and other app-generated line-item properties belong to the main
  // product form, while Sleek's bottom sticky ATC uses a separate product form.
  // Mirror those property values so every sticky placeholder stays in sync and
  // the bottom Add-to-cart submits the same selection as the main product form.
  function customPropertyEntries(mainForm, mainProductInfo) {
    const entries = new Map();
    const add = (name, value) => {
      if (!/^properties\[.+\]$/.test(name || '')) return;
      entries.set(name, String(value ?? ''));
    };

    try {
      new FormData(mainForm).forEach((value, name) => add(name, value));
    } catch (_) {}

    // Some option apps render the visible control outside the <form> and attach
    // it with JavaScript. Prefer the live visible/checked control when present.
    mainProductInfo?.querySelectorAll?.(
      'input[name^="properties["],select[name^="properties["],textarea[name^="properties["]'
    ).forEach((control) => {
      if (control.closest('sticky-atc-bar')) return;
      if ((control.type === 'radio' || control.type === 'checkbox') && !control.checked) return;
      add(control.name, control.value);
    });

    return entries;
  }

  function stickyPropertyControls(form, name) {
    return [...form.querySelectorAll(
      'input[name^="properties["],select[name^="properties["],textarea[name^="properties["]'
    )].filter((control) => control.name === name && control.dataset.ksPropertyMirror !== '1');
  }

  function updateStickyPropertyControls(controls, value) {
    if (!controls.length) return false;

    controls.forEach((control) => {
      let changed = false;

      if (control.type === 'radio' || control.type === 'checkbox') {
        const nextChecked = String(control.value) === String(value);
        changed = control.checked !== nextChecked;
        control.checked = nextChecked;
      } else {
        changed = String(control.value ?? '') !== String(value);
        control.value = value;
      }

      // Allow app-rendered labels/placeholders to react to the mirrored value.
      if (changed) {
        control.dispatchEvent(new Event('input', { bubbles: true }));
        control.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    return true;
  }

  function reconcileStickyPropertyMirror(stickyForm, name, value) {
    const controls = stickyPropertyControls(stickyForm, name);
    const mirrors = [...stickyForm.querySelectorAll('[data-ks-property-mirror="1"]')]
      .filter((input) => input.name === name);

    if (updateStickyPropertyControls(controls, value)) {
      mirrors.forEach((input) => input.remove());
      return;
    }

    let mirror = mirrors.shift();
    if (!mirror) {
      mirror = document.createElement('input');
      mirror.type = 'hidden';
      mirror.name = name;
      mirror.dataset.ksPropertyMirror = '1';
      stickyForm.appendChild(mirror);
    }
    if (mirror.value !== value) mirror.value = value;
    mirrors.forEach((input) => input.remove());
  }

  function syncStickyCustomProperties(sticky) {
    const stickyForm = sticky?.querySelector('form[is="product-form"]');
    const productId = sticky?.getAttribute('data-product-id');
    if (!stickyForm || !productId) return;

    const mainForm = document.querySelector(`.main-product-form[data-product-id="${productId}"]`);
    const mainProductInfo = document.querySelector(`product-info[data-product-id="${productId}"]`);
    if (!mainForm) return;

    const properties = customPropertyEntries(mainForm, mainProductInfo);

    // Remove mirrors for properties that no longer exist on the main form.
    stickyForm.querySelectorAll('[data-ks-property-mirror="1"]').forEach((input) => {
      if (!properties.has(input.name)) input.remove();
    });

    properties.forEach((value, name) => {
      reconcileStickyPropertyMirror(stickyForm, name, value);
    });
  }

  function syncAllStickyCustomProperties() {
    document.querySelectorAll('sticky-atc-bar').forEach(syncStickyCustomProperties);
  }

  function installStickyPropertySync() {
    if (document.documentElement.dataset.ksStickyPropertySync === '1') return;
    document.documentElement.dataset.ksStickyPropertySync = '1';

    const syncFromMain = (event) => {
      const control = event.target;
      if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement || control instanceof HTMLTextAreaElement)) return;
      if (!control.name?.startsWith('properties[')) return;
      if (control.closest('sticky-atc-bar')) return;
      queueMicrotask(syncAllStickyCustomProperties);
    };

    document.addEventListener('input', syncFromMain, true);
    document.addEventListener('change', syncFromMain, true);

    // Make the latest value available before the sticky product-form handler
    // reads/submits its fields.
    document.addEventListener('pointerdown', (event) => {
      const sticky = event.target.closest?.('sticky-atc-bar');
      if (!sticky || !event.target.closest?.('[name="add"]')) return;
      syncStickyCustomProperties(sticky);
    }, true);

    document.addEventListener('submit', (event) => {
      const sticky = event.target.closest?.('sticky-atc-bar');
      if (sticky) syncStickyCustomProperties(sticky);
    }, true);
  }

  function refresh() {
    removeLegacyMobileBottomNav();
    manageWhatsApp();
    installCartDrawerGuard();
    enhanceStickyVariantPicker();
    installStickyPropertySync();
    syncAllStickyCustomProperties();
  }

  function init() {
    refresh();
    document.addEventListener('shopify:section:load', refresh);

    const observer = new MutationObserver(mutations => {
      const hasLegacyNav = !!document.getElementById('KSMobileBottomNav');
      const relevant = hasLegacyNav || mutations.some(m => {
        const el = m.target?.nodeType === 1 ? m.target : m.target?.parentElement;
        return el && !el.closest?.('.ks-whatsapp-float');
      });
      if (!relevant) return;
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(refresh, 100);
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init, { once: true })
    : init();
})();
